import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Table2, Save, Plus, X, History } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const WOOD_TYPES = ["Mango", "Babool", "Sheesham", "Pine", "Oak", "Teak", "Other"];
const WOOD_LEN = Array.from({ length: 23 }, (_, i) => (1 + 0.5 * i).toFixed(1));   // 1.0 .. 12.0 ft
const WOOD_THK = ["1.0", "1.5", "2.0", "2.5", "3.0", "3.5"];                        // inches
const SHEET_ITEMS = ["MDF", "PLY", "PAPER VENEER", "MDF ONE SIDE VENEER", "MDF BOTH SIDE VENEER", "PLY ONE SIDE VENEER", "PLY BOTH SIDE VENEER"];
const SHEET_THK = Array.from({ length: 60 }, (_, i) => (0.5 + 0.5 * i).toFixed(1)); // 0.5 .. 30.0 mm

const CATS = [
  { key: "hardware", label: "Raw Hardware", hint: "₹ per piece" },
  { key: "polish", label: "Polish Materials", hint: "₹ per unit" },
  { key: "packing", label: "Packing Materials", hint: "₹ per unit" },
  { key: "final_hardware", label: "Final Hardware", hint: "₹ per piece" },
];
const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const blankRow = () => ({ id: uid(), item_name: "", item_type: "", size: "", other_spec: "", rate: "" });

const CatalogEditor = ({ cat, rows, onAdd, onUp, onRm }) => (
  <Card className="rounded-md border-border p-5 shadow-none" data-testid={`rate-card-${cat.key}`}>
    <div className="mb-3 flex items-center justify-between">
      <div>
        <h3 className="font-heading text-lg font-bold">{cat.label}</h3>
        <p className="text-xs text-muted-foreground">{cat.hint} · add the items you actually use</p>
      </div>
      <Button type="button" size="sm" variant="outline" className="h-8 gap-1 text-xs" data-testid={`rate-${cat.key}-add`} onClick={onAdd}>
        <Plus className="h-3 w-3" /> Add row</Button>
    </div>
    <div className="-mx-1 overflow-x-auto px-1"><div className="min-w-[640px]">
    <div className="grid grid-cols-12 gap-2 px-1 pb-1 text-[10px] font-semibold uppercase text-muted-foreground">
      <span className="col-span-3">Item name</span><span className="col-span-2">Item type</span>
      <span className="col-span-2">Size</span><span className="col-span-3">Other spec</span>
      <span className="col-span-1">Rate ₹</span><span className="col-span-1" />
    </div>
    {rows.length === 0 && <p className="px-1 py-2 text-xs text-muted-foreground">No items yet. Click “Add row”.</p>}
    {rows.map((r, i) => (
      <div key={r.id || i} className="mb-1 grid grid-cols-12 items-center gap-2">
        <Input className="col-span-3" data-testid={`rate-${cat.key}-name-${i}`} value={r.item_name} onChange={(e) => onUp(i, "item_name", e.target.value)} />
        <Input className="col-span-2" data-testid={`rate-${cat.key}-type-${i}`} value={r.item_type} onChange={(e) => onUp(i, "item_type", e.target.value)} />
        <Input className="col-span-2" data-testid={`rate-${cat.key}-size-${i}`} value={r.size} onChange={(e) => onUp(i, "size", e.target.value)} />
        <Input className="col-span-3" data-testid={`rate-${cat.key}-spec-${i}`} value={r.other_spec} onChange={(e) => onUp(i, "other_spec", e.target.value)} />
        <Input className="col-span-1" type="text" inputMode="decimal" data-testid={`rate-${cat.key}-rate-${i}`} value={r.rate ?? ""} onChange={(e) => onUp(i, "rate", e.target.value)} />
        <Button type="button" variant="ghost" size="icon" className="col-span-1 h-8 w-8" data-testid={`rate-${cat.key}-rm-${i}`} onClick={() => onRm(i)}><X className="h-4 w-4" /></Button>
      </div>
    ))}
    </div></div>
  </Card>
);

export default function RateSheets() {
  const [catalogs, setCatalogs] = useState({});
  const [wood, setWood] = useState({});    // {type: {"L_T": rate}}
  const [sheet, setSheet] = useState({});   // {"item_T": rate}
  const [woodType, setWoodType] = useState("Mango");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/rates").then((r) => {
      const d = r.data || {};
      setCatalogs(d.catalogs || {}); setWood(d.wood || {}); setSheet(d.sheet || {});
    });
  }, []);
  const loadHistory = () => api.get("/rates/history").then((r) => setHistory(r.data || []));

  const rowsOf = (k) => catalogs[k] || [];
  const addRow = (k) => setCatalogs((p) => ({ ...p, [k]: [...(p[k] || []), blankRow()] }));
  const upRow = (k, i, field, val) => setCatalogs((p) => {
    const c = [...(p[k] || [])]; c[i] = { ...c[i], [field]: val };
    return { ...p, [k]: c };
  });
  const rmRow = (k, i) => setCatalogs((p) => ({ ...p, [k]: (p[k] || []).filter((_, x) => x !== i) }));

  const setWoodRate = (t, L, T, v) => setWood((p) => ({ ...p, [t]: { ...(p[t] || {}), [`${L}_${T}`]: parseFloat(v) || 0 } }));
  const setSheetRate = (item, T, v) => setSheet((p) => ({ ...p, [`${item}_${T}`]: parseFloat(v) || 0 }));

  const save = async () => {
    setSaving(true);
    const catN = {};
    for (const [k, rows] of Object.entries(catalogs)) {
      catN[k] = (rows || []).map((r) => ({ ...r, rate: parseFloat(r.rate) || 0 }));
    }
    try { await api.put("/rates", { catalogs: catN, wood, sheet }); setCatalogs(catN); toast.success("Rates saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Rate Sheets" icon={Table2}
        subtitle="Wood & Ply/MDF rates by length/thickness; hardware, polish, packing & final-hardware as item lists. Used to auto-fill the cost sheet."
        action={<div className="flex gap-2">
          <Dialog onOpenChange={(v) => v && loadHistory()}>
            <DialogTrigger asChild><Button variant="outline" data-testid="rates-history" className="gap-2"><History className="h-4 w-4" /> History</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Rate change history</DialogTitle></DialogHeader>
              <div className="max-h-[60vh] space-y-2 overflow-y-auto">
                {history.length === 0 && <p className="text-sm text-muted-foreground">No history yet. Snapshots are saved automatically each time you press Save Rates.</p>}
                {history.map((h) => (
                  <div key={h.id} className="rounded-md border border-border p-3 text-sm" data-testid="rate-history-row">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{new Date(h.saved_at).toLocaleString()}</span>
                      <span className="text-xs text-muted-foreground">by {h.saved_by}</span>
                    </div>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
          <Button data-testid="rates-save" onClick={save} disabled={saving} className="gap-2"><Save className="h-4 w-4" /> Save Rates</Button>
        </div>} />

      {/* Wood matrix */}
      <Card className="rounded-md border-border p-5 shadow-none" data-testid="rate-card-wood">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-bold">Solid Wood (₹ per board-ft)</h3>
            <p className="text-xs text-muted-foreground">Length (ft) rows × Thickness (in) columns, per wood type</p>
          </div>
          <Select value={woodType} onValueChange={setWoodType}>
            <SelectTrigger className="w-48" data-testid="rate-wood-type"><SelectValue /></SelectTrigger>
            <SelectContent>{WOOD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="overflow-auto">
          <table className="text-xs">
            <thead>
              <tr>
                <th className="sticky left-0 bg-card p-1 text-left text-muted-foreground">LENGTH\THICKNESS</th>
                {WOOD_THK.map((t) => <th key={t} className="p-1 text-muted-foreground">{t}"</th>)}
              </tr>
            </thead>
            <tbody>
              {WOOD_LEN.map((L) => (
                <tr key={L}>
                  <td className="sticky left-0 bg-card p-1 font-semibold">{L}</td>
                  {WOOD_THK.map((T) => (
                    <td key={T} className="p-0.5">
                      <Input className="h-8 w-16 px-1 text-xs" type="number" value={wood[woodType]?.[`${L}_${T}`] ?? ""}
                        onChange={(e) => setWoodRate(woodType, L, T, e.target.value)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Ply / MDF matrix */}
      <Card className="rounded-md border-border p-5 shadow-none" data-testid="rate-card-sheet">
        <h3 className="mb-1 font-heading text-lg font-bold">Ply / MDF (₹ per sq-ft)</h3>
        <p className="mb-4 text-xs text-muted-foreground">Item rows × Thickness (mm) columns</p>
        <div className="overflow-auto">
          <table className="text-xs">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-card p-1 text-left text-muted-foreground whitespace-nowrap">ITEM\THICKNESS (mm)</th>
                {SHEET_THK.map((t) => <th key={t} className="p-1 text-muted-foreground">{t}</th>)}
              </tr>
            </thead>
            <tbody>
              {SHEET_ITEMS.map((item) => (
                <tr key={item}>
                  <td className="sticky left-0 z-10 bg-card p-1 font-semibold whitespace-nowrap">{item}</td>
                  {SHEET_THK.map((T) => (
                    <td key={T} className="p-0.5">
                      <Input className="h-8 w-16 px-1 text-xs" type="number" value={sheet[`${item}_${T}`] ?? ""}
                        onChange={(e) => setSheetRate(item, T, e.target.value)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {CATS.map((cat) => (
        <CatalogEditor key={cat.key} cat={cat} rows={rowsOf(cat.key)}
          onAdd={() => addRow(cat.key)} onUp={(i, f, v) => upRow(cat.key, i, f, v)} onRm={(i) => rmRow(cat.key, i)} />
      ))}
    </div>
  );
}
